import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useDispatch } from 'react-redux'

import map from 'lodash/map'
import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { login } from '@galo-monitor/icons'
import api from '@galo-monitor/services/apis/galoMonitorApi'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { CompanyActions } from '@galo-monitor/stores/company'
import { ContextActions } from '@galo-monitor/stores/context'
import { FarmActions } from '@galo-monitor/stores/farm'
import { colors } from '@galo-monitor/styles'
import DataTable from '@galo-monitor/web-components/DataTable'
import Icon from '@galo-monitor/web-components/Icon'
import Screen from '@galo-monitor/web-components/Screen'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const UserFarmsScreen = () => {
  const t = useT()
  const { createDialog } = useDialog()

  const dispatch = useCallback(useDispatch(), [])
  const { routes } = useRoutes()

  const [loading, setLoading] = useState(true)
  const [filtersValues, setFiltersValues] = useState({})

  const asyncFarmsData = useCallback(
    params => api.get(
      '/companies/farms/my-access',
      {
        params
      }
    ),
    []
  )

  const { data: segments } = useGaloMonitorApi('/companies/farms/segments')
  const { data: plans } = useGaloMonitorApi('/companies/plans')

  const segmentsOptions = useMemo(
    () => segments?.length > 0
      ? map(segments, ({ id, name }) => ({
        value: id.toString(),
        label: name
      }))
      : [],
    [segments]
  )

  const plansOptions = useMemo(
    () => plans?.length > 0
      ? map(plans, ({ id, name }) => ({
        value: id.toString(),
        label: name
      }))
      : [],
    [plans]
  )

  const columns = useMemo(
    () => [
      {
        title: t('account', { howMany: 1 }),
        field: 'name'
      },
      {
        title: t('responsible'),
        field: 'nameResponsible'
      },
      {
        title: t('segment', { howMany: 2 }),
        field: 'segment.name'
      },
      {
        title: t('plan', { howMany: 2 }),
        field: 'plan.name'
      },
      {
        title: t('property', { howMany: 2 }),
        field: 'meta.propertiesCount',
        numeric: true
      },
      {
        title: t('plot', { howMany: 2 }),
        field: 'meta.plotsCount',
        numeric: true
      }
    ],
    [t]
  )

  const onChangeFilterValue = (newValue, filter) =>
    setFiltersValues(
      prevState => ({
        ...prevState,
        [filter]: newValue
      })
    )

  const filters = useMemo(() => [
    {
      id: 'segment',
      name: 'segment',
      label: t('segment', { howMany: 1 }),
      options: segmentsOptions,
      value: filtersValues.segment,
      onChange: newValue => onChangeFilterValue(newValue, 'segment'),
      customAutocompleteStyles: {
        flex: '0 0 160px'
      }
    },
    {
      id: 'plan',
      name: 'plan',
      label: t('plan', { howMany: 1 }),
      options: plansOptions,
      value: filtersValues.plan,
      onChange: newValue => onChangeFilterValue(newValue, 'plan'),
      customAutocompleteStyles: {
        flex: '0 0 160px'
      }
    }
  ], [filtersValues, plansOptions, segmentsOptions, t])

  const actions = useMemo(
    () => {
      const datatableActions = []

      datatableActions.push({
        icon: () => <Icon icon={ login } color={ colors.darkGrey } />,
        tooltip: t('datagrid body access tooltip'),
        onClick: (_, farm) => {
          dispatch(CompanyActions.setDefaultFarm(
            farm.id,
            () => {
              dispatch(FarmActions.setCurrentFarm(farm))
              dispatch(ContextActions.initContextFarm(farm.id))
            },
            () => createDialog({
              id: 'access-farm-error-dialog',
              Component: ErrorModal,
              props: {
                title: t('could not {action} the {this}', {
                  action: toLower(t('to access')),
                  gender: 'female',
                  howMany: 1,
                  this: `${ t('farm', { howMany: 1 }) } ${ farm.id }`
                })
              }
            })
          ))
        },
        iconButtonProps: {
          size: 'small'
        },
        position: 'row'
      })

      return datatableActions
    },
    [createDialog, dispatch, t]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.userFarms.title,
      key: 'farm-access-screen',
      url: '/farm-access',
      active: true
    }
  ], [routes.userFarms.title])

  useEffect(() => {
    setLoading(true)
    dispatch(FarmActions.loadUserFarms(
      () => setLoading(false),
      () => setLoading(false)
    ))
  }, [dispatch])

  return (
    <Screen title={ routes.userFarms.title } breadcrumbs={ crumbs }>
      <DataTable
        data={ asyncFarmsData }
        actions={ actions }
        columns={ columns }
        filters={ filters }
        loading={ loading }
      />
    </Screen>
  )
}

export default UserFarmsScreen
